<!--
  @fileoverview Slider Component - Rating/Feedback Slider (Vue 3)

  Renders a draggable slider for collecting numeric ratings or feedback.
  The slider displays a gradient of colored segments and a draggable dot
  that users can move to select their rating.

  @component MessageSlider
  @displayName Rating Slider

  Features:
  - Draggable slider dot (supports mouse and touch)
  - Configurable number of units/segments
  - Configurable min/max text labels
  - Customizable main color for segments
  - Confirm button to submit rating
  - Markdown support for message text

  @example
  <MessageSlider
    :message="messageWithSliderConfig"
    :button-outline="'primary'"
    :is-user-message="false"
    :agents="agentsList"
    :should-disable-if-in-livechat="false"
    @sendPostback="handleSliderSubmit"
  />

  @vue3migration
  - Migrated to script setup syntax
  - Replaced mapGetters with useMainStore()
  - Replaced this.$store.getters.settings with store.settings
  - Replaced this.$options.filters.markHTML with direct import from @/filters
  - Replaced this.$set() with direct assignment (Vue 3 reactivity)
  - Replaced this.$forceUpdate() (not needed in Vue 3 with proper reactivity)
  - Added proper TypeScript-style prop definitions
-->
<template>
  <div class="bubble-wrapper" v-if="text">
    <div class="message-wrapper message-bubble-left">
      <p class="!mb-0 break-words" v-html="markedText"></p>
    </div>
    <div
      class="message-slider text-right"
      :style="{ '--slider-main-color': sliderConfig.mainColor }"
    >
      <div class="slider-text">
        <span>{{ sliderConfig.minText }}</span>
        <span class="slider-max-text">{{
          sliderConfig.maxText
        }}</span>
      </div>
      <div class="relative">
        <div class="slider-group" ref="sliderGroup">
          <div
            class="slider-item"
            v-for="(unit, index) in sliderConfig.units"
            :key="index"
            :style="sliderItemCssVars(index)"
          ></div>
        </div>
        <div
          class="slider-dot"
          ref="sliderDot"
          @mousedown.prevent="isSliderDraggable = true"
          @touchstart="isSliderDraggable = true"
        >
          {{ selectedUnit }}
        </div>
      </div>
      <!-- @vue3migration: Removed size="small" - Element Plus default size is equivalent -->
      <el-button
        plain
        class="confirm-button"
        :type="buttonOutline"
        :class="{ 'blue-button': buttonOutline === 'primary' }"
        @click="handleSliderConfirm"
      >
        Confirm
      </el-button>
    </div>
  </div>
</template>

<script setup>
/**
 * @module components/ChatWindow/Message/Slider
 * @description Rating slider component for collecting numeric feedback (Vue 3 Composition API)
 */
import { ref, computed, onMounted } from 'vue'
import _ from 'lodash-es'
import dayjs from 'dayjs'
import { useMainStore } from '@/stores/main'
import { markHTML } from '@/filters'

/**
 * Component props with proper types
 */
const props = defineProps({
  /**
   * Message object containing slider configuration
   */
  message: {
    type: Object,
    required: true,
  },
  /**
   * Button outline style/type
   */
  buttonOutline: {
    type: String,
    default: '',
  },
  /**
   * Whether this is a user message
   */
  isUserMessage: {
    type: Boolean,
    default: false,
  },
  /**
   * List of agents (for reply attribution)
   */
  agents: {
    type: Array,
    default: () => [],
  },
  /**
   * Whether to disable buttons when in live chat mode
   */
  shouldDisableIfInLivechat: {
    type: Boolean,
    default: false,
  },
})

/**
 * Component emits
 * @event sendPostback - Emits slider value to parent for processing
 */
const emit = defineEmits(['sendPostback'])

/**
 * Pinia store instance
 */
const store = useMainStore()

/**
 * Template refs for slider elements
 */
const sliderGroup = ref(null)
const sliderDot = ref(null)

/**
 * Reactive state
 */
const isResendButtonDisabled = ref(false)
const isMessageSent = ref(true)
const isSliderDraggable = ref(false)
const selectedUnit = ref(1)
const visible = ref(false)

/**
 * Check if RTL mode is enabled from store
 */
const isRtlModeEnabled = computed(() => store.isRtlModeEnabled)

/**
 * Access webchat settings from Pinia store
 */
const settings = computed(() => store.settings)

/**
 * Message content text getter
 * @returns {string|null} The message text or postback text
 */
const text = computed(() => {
  return (
    _.get(props.message, 'data.content[0].text', null) ||
    _.get(props.message, 'data.content[0].postbackText', null)
  )
})

/**
 * Sanitize and render markdown content as HTML
 * @returns {string} Sanitized HTML string
 */
const markedText = computed(() => {
  return markHTML(text.value, props.userTextColor)
})

/**
 * Slider configuration from message data
 * @returns {Object|null} Slider config object
 */
const sliderConfig = computed(() => {
  return _.get(props.message, 'data.content[0].sliderConfig', null)
})

/**
 * Node event configuration for postback
 * @returns {Object} Node event object with key, next, event
 */
const nodeEvent = computed(() => {
  return props.message.data.content[0].sliderConfig.nodeEvent
})

/**
 * Get timestamp string for message
 * @param {Object} message - Message object
 * @returns {string} Formatted timestamp string
 */
function timestamp(message) {
  const source = props.isUserMessage ? 'You' : props.replyName
  return `${source} at ${dayjs(message.timestamp).format('D MMM, hh:mma')}`
}

/**
 * Update message status (sent/failed)
 * @param {boolean} status - New status value
 */
function updateMessageStatus(status) {
  isMessageSent.value = status || status === undefined
  isResendButtonDisabled.value = false
}

/**
 * Check if string only contains whitespace
 * @param {string} str - String to check
 * @returns {boolean} True if only whitespace
 */
function isOnlyWhiteSpace(str) {
  return !str.replace(/\s/g, '').length
}

/**
 * Calculate CSS variables for individual slider segment (CSP-compliant)
 * @param {number} index - Segment index
 * @returns {Object} CSS custom properties object
 */
function sliderItemCssVars(index) {
  return {
    '--slider-item-opacity': (100 / sliderConfig.value.units) * (index + 1) + '%',
    '--slider-item-flex-basis': 100 / sliderConfig.value.units + '%',
  }
}

/**
 * Handle slider drag movement
 * @param {MouseEvent|TouchEvent} e - Drag event
 */
function handleSliderChange(e) {
  e.preventDefault
  const sliderEl = sliderGroup.value
  const sliderDotEl = sliderDot.value
  const eventPosition = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX
  if (isSliderDraggable.value) {
    const sliderElDOMRect = sliderEl.getBoundingClientRect()
    const sliderDotPosition = eventPosition - sliderElDOMRect.x
    if (sliderDotPosition < sliderElDOMRect.width && sliderDotPosition > 0) {
      let percent = (sliderDotPosition / sliderElDOMRect.width) * 100
      if (isRtlModeEnabled.value) {
        percent = 100 - percent
        sliderDotEl.style.right = percent + '%'
      } else {
        sliderDotEl.style.left = percent + '%'
      }
      selectedUnit.value = Math.ceil(percent / (100 / sliderConfig.value.units))
    }
  }
}

/**
 * Handle confirm button click - submit the slider value
 */
function handleSliderConfirm() {
  const { key, next, event } = nodeEvent.value
  const reply = {
    active: true,
    data: {
      key,
      next,
      value: selectedUnit.value,
    },
    event,
    text: nodeEvent.value.text || 'Confirm rating',
  }
  emit('sendPostback', reply)
}

/**
 * Set up event listeners on mount
 */
onMounted(() => {
  document.addEventListener('mousemove', handleSliderChange)
  document.addEventListener('touchmove', handleSliderChange)
  document.addEventListener('mouseup', () => {
    isSliderDraggable.value = false
  })
  document.addEventListener('touchend', () => {
    isSliderDraggable.value = false
  })
})
</script>

<style lang="scss" scoped>
.message-wrapper {
  display: inline-block;
  *:not(button) {
    font-size: 16px !important;
  }

  p {
    margin-top: 0;
    margin-bottom: 16px !important;
    line-height: 1.2;
    padding-bottom: 0;
    &:last-child {
      margin-bottom: 0 !important;
    }
  }

  ul,
  ol {
    padding: revert;
  }

  li {
    margin-top: 0;
    margin-bottom: 5px;
  }

  a {
    text-decoration: underline !important;
  }

  a,
  a:visited,
  p,
  li {
    color: inherit;
  }

  img {
    border-radius: 15px;
  }
}

[dir="ltr"] .message-wrapper {
  li {
    margin-left: 2px;
  }
}

[dir="rtl"] .message-wrapper {
  li {
    margin-right: 2px;
  }
}

.message-bubble-status {
  font-size: 16px;
  position: absolute;
  bottom: 3px;
  z-index: 10;
  font-weight: bold;
}

[dir="ltr"] .message-bubble-status {
  right: 2px;
}

[dir="rtl"] .message-bubble-status {
  left: 2px;
}

.message-bubble-left {
  position: relative;
  padding: 16px;
  color: var(--keyreply-agent-text-color) !important;
  background: var(--keyreply-agent-bubble-color);
  max-width: 70%;
  margin: 0;
  font-family: var(--keyreply-font-family);
}

[dir="ltr"] .message-bubble-left {
  border-radius: 16px 16px 16px 0;
  text-align: left;
}

[dir="rtl"] .message-bubble-left {
  border-radius: 16px 16px 0 16px;
  text-align: right;
}

.bubble-wrapper {
  display: flex;
  flex-direction: column;
  margin: 8px 16px;
  align-items: flex-start;
}

.message-slider {
  width: 70%;

  .slider-text {
    margin: 8px 0;
    display: flex;
    justify-content: space-between;
    font-size: 12px;
    font-weight: bold;

    .slider-max-text {
      color: var(--slider-main-color);
    }
  }

  .slider-group {
    display: flex;
    border-radius: 50px;
    overflow: hidden;
    cursor: grab;

    .slider-item {
      height: 8px;
      background-color: var(--slider-main-color);
      opacity: var(--slider-item-opacity);
      flex-basis: var(--slider-item-flex-basis);
    }
  }
  .slider-dot {
    position: absolute;
    top: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 200px;
    font-size: 16px;
    color: #fff;
    cursor: grab;
    background-color: var(--slider-main-color);

    &:active {
      cursor: grabbing;
    }
  }
  .confirm-button {
    margin: 12px 0;
    border-radius: 8px;
    font-size: 16px;

    &.blue-button {
      color: #4e6cce;
      background: #dce2f5;
      border-color: #4e6cce;

      &:hover {
        background: #4e6cce;
        color: #fff;
      }
    }

    &.active {
      background: transparent;
    }
  }
}

[dir="ltr"] .message-slider {
  .slider-dot {
    left: 10%;
    transform: translate(-50%, -50%);
  }
  .confirm-icon {
    text-align: right;
  }
}

[dir="rtl"] .message-slider {
  .slider-dot {
    right: 10%;
    transform: translate(50%, -50%);
  }
  .confirm-icon {
    text-align: left;
  }
}
</style>

<style>
.message-bubble-left .el-select__input {
  background: transparent !important;
  border: none !important;
}
</style>
